import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import { X, Calendar, User } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function News() {
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState(null);
  useSEO({
    title: "News & Updates — Maqam",
    description: "Maqam product updates, Saudi job-market insights, and career news from the Kingdom.",
    path: "/news",
  });

  useEffect(() => {
    api.get("/news").then((r) => setPosts(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-white text-black">
      <PublicHeader />

      <section className="border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">/ News</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-3xl">
            Product updates, <span className="text-gradient">hiring intel</span>, Kingdom-wide insights.
          </h1>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          {posts.length === 0 ? (
            <div className="text-gray-500 font-mono text-sm">loading…</div>
          ) : (
            <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10" data-testid="news-grid">
              {posts.map((p) => (
                <article
                  key={p.id}
                  onClick={() => setActive(p)}
                  className="bg-white hover:bg-gray-50 transition-colors cursor-pointer flex flex-col"
                  data-testid={`news-card-${p.id}`}
                >
                  <div
                    className="h-44 bg-cover bg-center border-b border-black/5"
                    style={{ backgroundImage: `url(${p.cover})` }}
                  />
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-3">
                      {p.category}
                    </div>
                    <h3 className="font-display font-bold text-xl tracking-tight mb-3 leading-tight">{p.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed mb-6 flex-1">{p.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 font-mono">
                      <User className="w-3.5 h-3.5" />
                      {p.author}
                      <span>·</span>
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(p.published_at).toLocaleDateString()}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="news-modal">
          <div className="bg-white w-full max-w-3xl border border-black/10 max-h-[92vh] flex flex-col">
            <div className="relative">
              <div
                className="h-64 bg-cover bg-center"
                style={{ backgroundImage: `url(${active.cover})` }}
              />
              <button onClick={() => setActive(null)} className="absolute top-3 end-3 bg-white p-2 border border-black/20 hover:bg-black hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-8 overflow-y-auto">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-3">{active.category}</div>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter leading-tight mb-4">{active.title}</h2>
              <div className="flex items-center gap-3 text-xs text-gray-500 font-mono mb-6">
                <span>{active.author}</span>
                <span>·</span>
                <span>{new Date(active.published_at).toLocaleDateString()}</span>
              </div>
              <p className="text-gray-800 leading-relaxed">{active.body}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
